<template>
    <v-col v-if="current_account">
        <v-row no-gutters>
            <v-col
                v-for="(block, index) in content"
                :key="'b' + index"
                cols="12"
                class="pb-5 pt-2"
            >
                <v-row
                    no-gutters
                    align="baseline"
                    justify="space-between"
                    class="pr-2"
                >
                    <v-col cols="3" class="title">
                        {{ block.title }}
                    </v-col>
                    <template v-if="block.type === 'agreements'">
                        <v-col cols="3">
                            <dropdown
                                v-if="flextimeCompanies.length > 1"
                                :items="flextimeCompanies"
                                :selected="flextimeCompanies.filter(x => x.id === selectedCompanyId)"
                                :disabled="flextimeCompanies.length <= 1"
                                name-key="name"
                                value-key="id"
                                show-selected-first
                                :label="$t('Company')"
                                @input="onSelectCompanyAccount"
                            />
                            <div v-else-if="flextimeCompanies.find(x => x.id === selectedCompanyId)" class="body-2">
                                {{ flextimeCompanies.find(x => x.id === selectedCompanyId).name }}
                            </div>
                        </v-col>
                        <v-col cols="6" class="d-flex">
                            <div v-if="canEditCompany" class="ml-auto">
                                <secondary-button color="primary" @click="isAgreementsModalVisible = true">
                                    {{ $t('Change agreement') }}
                                </secondary-button>
                            </div>
                        </v-col>
                    </template>
                    <template v-else>
                        <v-col class="shrink">
                            <router-link
                                v-if="block.edit_link"
                                :to="{ name: 'profile_edit' }"
                                class="text-decoration-none"
                            >
                                <secondary-button color="primary">
                                    {{ $t("Change") }}
                                </secondary-button>
                            </router-link>
                        </v-col>
                    </template>
                </v-row>
                <v-divider class="mb-4 mt-1"/>
                <v-row
                    v-if="block.component && block.component_container"
                    no-gutters
                >
                    <v-col>
                        <component
                            :is="block.component"
                            v-bind="block.props"
                        />
                    </v-col>
                </v-row>
                <component
                    :is="block.component"
                    v-else-if="block.component"
                    v-bind="block.props"
                />
                <v-row
                    v-for="(row, indx) in block.rows"
                    :key="'r' + indx"
                    align="center"
                    no-gutters
                    class="my-3"
                >
                    <template>
                        <v-col
                            cols="3"
                            class="body-2 grey--text text--darken-1 text-sm-left"
                        >
                            {{ row.label }}
                        </v-col>
                        <v-col
                            class="text-sm-left"
                        >
                            <div
                                v-if="row.component"
                                :style="row.maxWidth ? `max-width: ${row.maxWidth}px` : ''"
                            >
                                <component
                                    :is="row.component"
                                    v-bind="row.props"
                                    @click="row.click ? row.click($event) : null"
                                />
                            </div>
                            <div v-else>
                                {{ row.content }}
                            </div>
                            <div
                                v-if="row.description"
                                class="body-2 grey--text text--darken-1 text-sm-left"
                            >
                                {{ row.description }}
                            </div>
                        </v-col>
                    </template>
                </v-row>
                <template v-if="block.type === 'profile_footer'">
                    <div class="d-flex flex-column align-start ml-n4">
                        <SecondaryButton
                            v-if="current_account.data && !current_account.data.show_demo"
                            color="main"
                            :loading="isLoading"
                            @click="start_demo"
                        >
                            {{ $t('Start demo') }}
                        </SecondaryButton>
                        <SecondaryButton
                            v-else
                            color="main"
                            :loading="isLoading"
                            @click="end_demo"
                        >
                            {{ $t('Stop demo') }}
                        </SecondaryButton>
                        <!--
                        <danger-button @click="onDeleteAccountClick">
                            {{ $t("Delete my account") }}
                        </danger-button>
                        -->
                    </div>
                </template>
            </v-col>
        </v-row>
        <AgreementsModal
            v-if="isAgreementsModalVisible"
            :person-id="personId"
            :person-name="`${profile.person_settings.first_name ? profile.person_settings.first_name : ''} ${profile.person_settings.surname ? profile.person_settings.surname : ''}`"
            :company-id="selectedCompanyId"
            @close="isAgreementsModalVisible = false"
            @update="fetchFlextimeSettings"
        />
    </v-col>
</template>

<script>
import {computed, onMounted, ref} from "@vue/composition-api";
import {$t} from "@/i18n";
import AccountModule from "@/store/modules/account";
import DialogModule from "@/store/modules/dialog";
import AccountAvatar from "@/components/AccountAvatar";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import ApiDemo from '@/api/demo/demo'
import ApiConnect from "@/api/account_setup/connect";
import LoginIntegrationButton from "@/components/Buttons/LoginIntegrationButton";
import auth from "@/modules/auth/auth";
import AgreementsModal from "@/views/Profile/Preview/AgreementsModal";
import ApiProfile from "@/api/profile/profile"
import {formatDate, formatTimeDuration, parseSysDate} from "@/plugins/formatting";
import {Router} from "@/composables/Router";
import RightsModule from "@/composables/use-rights";
import {uku_send_event, UKU_ACTIVITIES} from "@/helpers/uku_activity";
import ExternalConnectionsBlock from '../ExternalConnectionsBlock.vue'

export default {
    name: "ProfilePreview",
    components: {
        ExternalConnectionsBlock,
        AccountAvatar,
        LoginIntegrationButton,
        AgreementsModal
    },
    props: {
        personId: {
            type: Number,
            default: 0
        },
        profile: {
            type: Object,
            required: true
        }
    },
    setup(props) {
        const router = Router();
        const {openConfirmDialog, setConfirmOptions, openError, setErrorOptions} = DialogModule();
        const {can_edit_account} = RightsModule();
        const {
            deleteAccount,
            current_account,
        } = AccountModule();

        const isAgreementsModalVisible = ref(false)

        const flextimeCompanies = computed(() => props.profile.companies.filter(x => x.flextime_active))

        const selectedCompanyId = ref(flextimeCompanies.value.length ? flextimeCompanies.value[0].id : null);
        if (router.currentRoute.params.companyId) {
            selectedCompanyId.value = router.currentRoute.params.companyId
        }

        const canEditCompany = computed(() => can_edit_account(selectedCompanyId.value))

        const flexTimeSettings = ref(null);
        const companyFlexTimeSettings = ref(null);

        const isLoading = ref(false);
        const isGoogleConnectAllowed = ref(false);
        const isMicrosoftConnectAllowed = ref(false);
        const isXeroConnectAllowed = ref(false);

        const show_f = conditional => {
            if (conditional) {
                return $t("Show");
            } else {
                return $t("Don't show");
            }
        };

        const send_f = (key, conditional) => {
            if (!conditional) {
                return $t("Don't send");
            }
            const opts = {
                "is_daily_tasks_active": $t("Email workdays at 7 AM"),
                "is_daily_notifications_active": $t("Email at 3 PM"),
                "is_assigned_email_active": $t("Email at once"),
                "is_weekly_summary_active": $t("Email Sundays at 11 PM"),
                "is_monthly_summary_active": $t("First day of the month"),
                "newsletter": $t("to email"),
                "is_task_reminder_active": $t("E-mail at once")
            };
            return opts[key];
        };

        const onDeleteAccountConfirm = async () => {
            try {
                await deleteAccount(current_account.value.id)
                await uku_send_event({event: UKU_ACTIVITIES.ACCOUNT_DELETED, info: "Profile/Index.onDeleteAccountConfirm"})
                window.location.href = "/login";
            } catch (ex) {
                errorDialog.showError(ex);
            }
        };
        const onDeleteAccountClick = () => {
            setConfirmOptions({
                title: $t("Attention"),
                text: $t("Are you sure that you want to delete your account?"),
                confirm_text: $t("Confirm"),
                cancel_text: $t("Cancel"),
                action: onDeleteAccountConfirm
            });
            openConfirmDialog();
        };

        const getLockTimeEntriesNotificationText = () => {
            if (!props.profile.email_settings.lock_time_entries_send_reminder_enabled) {
                return $t("Don't send")
            }

            if (props.profile.email_settings.lock_time_entries_send_reminder_email_enabled && props.profile.email_settings.lock_time_entries_send_reminder_notification_enabled) {
                return $t('Notification & email')
            } else if (props.profile.email_settings.lock_time_entries_send_reminder_email_enabled) {
                return $t('Email')
            } else if (props.profile.email_settings.lock_time_entries_send_reminder_notification_enabled) {
                return $t('Notification')
            }

            return $t("Don't send")
        }

        const formatPeriod = (item) => {
            const startDate = item.settings_owner_date_from ? formatDate(parseSysDate(item.settings_owner_date_from)) : '...'
            const endDate = item.settings_owner_date_until ? formatDate(parseSysDate(item.settings_owner_date_until)) : '...'
            let type = $t('System')

            if (item.settings_owner_type === 'settings_owner_type_company') {
                type = $t('Company')
            } else if (item.settings_owner_type === 'settings_owner_type_person') {
                type = $t('Person')
            }

            if (!item.settings_owner_date_from && !item.settings_owner_date_until) {
                return type
            }

            return `${type} ${startDate} - ${endDate}`
        }

        const content = computed(() => {
            let canEditProfile = props.personId === current_account.value.id
            return [
                {
                    title: $t("Your details"),
                    edit_link: canEditProfile ? "edit" : false,
                    rows: [
                        {
                            label: $t("Picture"),
                            component: AccountAvatar,
                            props: {
                                size: 50,
                                imagePath: props.profile.person_settings.avatar,
                                initials: props.profile.person_settings.initials,
                                color: props.profile.person_settings.avatar ? "white" : "neutral_n40",
                                "text-color": "neutral_n300",
                                "text-class": "font-weight-medium"
                            }
                        },
                        {
                            label: $t("Name"),
                            content: props.profile.person_settings.first_name + " " + props.profile.person_settings.surname
                        },
                        {label: $t("Email"), content: props.profile.person_settings.email},
                        {label: $t("Time zone"), content: props.profile.person_settings.timezone}
                    ]
                },
                {
                    title: $t("Calendar settings"),
                    rows: [
                        {
                            label: $t("Public holidays"),
                            content: show_f(props.profile.calendar_settings.show_holidays)
                        },
                        {
                            label: $t("Tax deadlines"),
                            content: show_f(props.profile.calendar_settings.show_tax_dates)
                        },
                        {
                            label: $t("News"),
                            content: show_f(props.profile.calendar_settings.show_news),
                            key: "show_news",
                            id: "show_news_intl"
                        }
                    ]
                },
                {
                    title: $t("Emails"),
                    rows: [
                        {
                            label: $t("Daily agenda"),
                            content: send_f("is_daily_tasks_active", props.profile.email_settings.is_daily_tasks_active)
                        },
                        {
                            label: $t("Unread notifications"),
                            content: send_f("is_daily_notifications_active", props.profile.email_settings.is_daily_notifications_active)
                        },
                        {
                            label: $t("Task assigned to me"),
                            content: send_f("is_assigned_email_active", props.profile.email_settings.is_assigned_email_active)
                        },
                        {
                            label: $t("Weekly summary"),
                            content: send_f("is_weekly_summary_active", props.profile.email_settings.is_weekly_summary_active)
                        },
                        {
                            label: $t("Monthly summary"),
                            content: send_f("is_monthly_summary_active", props.profile.email_settings.is_monthly_summary_active)
                        },
                        {
                            label: $t("Updates and tips"),
                            content: send_f("newsletter", props.profile.email_settings.newsletter)
                        },
                        {
                            label: $t("Lock time entries notification"),
                            content: getLockTimeEntriesNotificationText()
                        }
                    ]
                },
                current_account.value.id === props.personId && (isGoogleConnectAllowed.value || isMicrosoftConnectAllowed.value || isXeroConnectAllowed.value) ? {
                    component: ExternalConnectionsBlock,
                    type: 'external_connections',
                    title: $t("External services"),
                    edit_link: "",
                    props: {
                        personId: props.personId,
                        googleAllowed: isGoogleConnectAllowed.value,
                        microsoftAllowed: isMicrosoftConnectAllowed.value,
                        xeroAllowed: isXeroConnectAllowed.value
                    }
                } : null,
                flexTimeSettings.value ? {
                    title: $t("Agreement"),
                    edit_link: "agreements",
                    type: "agreements",
                    rows: [
                        {
                            label: $t("Active agreement"),
                            content: formatPeriod(flexTimeSettings.value)
                        },
                        {
                            label: $t("Weekly hours"),
                            content: getTotalWorkingHourDuration(flexTimeSettings.value)
                        },
                        {
                            label: $t("Work on holidays"),
                            content: flexTimeSettings.value.work_holidays ? $t('Yes') : $t('No')
                        },
                    ]
                } : null,
                current_account.value.id === props.personId ? {
                    type: "profile_footer"
                } : null
            ].filter(x => x);
        })

        const start_demo = async () => {
            try {
                isLoading.value = true;
                await ApiDemo.startDemo();
                window.location = "/";
            } catch (err) {
                console.error("Start demo", err);
            } finally {
                isLoading.value = false;
            }
        };

        const end_demo = async () => {
            try {
                isLoading.value = true;
                await ApiDemo.endDemo();
                window.location = "/";
            } catch (err) {
                console.error("End demo", err);
            } finally {
                isLoading.value = false;
            }
        };

        const fetchFlextimeSettings = async () => {
            if (!selectedCompanyId.value) {
                return
            }

            try {
                const data = await ApiProfile.getProfileFlextimeSettings(selectedCompanyId.value, props.personId)
                flexTimeSettings.value = data.person_flextime_settings
                companyFlexTimeSettings.value = data.company_flextime_settings
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        onMounted(async () => {
            try {
                await fetchFlextimeSettings()

                if (current_account.value.id === props.personId) {
                    const allowedLoginOptions = await ApiConnect.getAllowedLoginOptions()

                    isMicrosoftConnectAllowed.value = allowedLoginOptions.microsoft_allowed
                    isGoogleConnectAllowed.value = allowedLoginOptions.google_allowed
                    isXeroConnectAllowed.value = allowedLoginOptions.xero_allowed
                }
            } catch (e) {
                errorDialog.showError(e)
            }
        })

        const onSelectCompanyAccount = (company) => {
            selectedCompanyId.value = company.id
            fetchFlextimeSettings()
        }

         const getTotalWorkingHourDuration = (item) => {
            if (!item._data) {
                return 0
            }

            let total = 0

            total += item._data.monday
            total += item._data.tuesday
            total += item._data.wednesday
            total += item._data.thursday
            total += item._data.friday
            total += item._data.saturday
            total += item._data.sunday



            return formatTimeDuration(total, '', true, true)
        }

        return {
            show_f,
            send_f,
            content,
            onDeleteAccountClick,
            current_account,
            isLoading,
            start_demo,
            end_demo,
            isMicrosoftConnectAllowed,
            auth,
            isAgreementsModalVisible,
            selectedCompanyId,
            onSelectCompanyAccount,
            fetchFlextimeSettings,
            flextimeCompanies,
            flexTimeSettings,
            companyFlexTimeSettings,
            canEditCompany,
            getTotalWorkingHourDuration
        };
    }
};
</script>
